import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  X, User, Heart, MessageCircle, Gavel, LogOut, Crown, Building2, Plus,
  Home as HomeIcon, Shield, FileText, LogIn, UserPlus, Tag,
} from "lucide-react";
import { CATEGORIES } from "../constants";
import { useAuth } from "../context/AuthContext";

export default function MobileMenu({ open, onClose }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const go = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    logout();
    onClose();
    navigate("/");
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-[60] md:hidden transition-opacity ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 bottom-0 z-[70] w-[85%] max-w-sm bg-white shadow-2xl md:hidden transform transition-transform duration-300 flex flex-col ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="mobile-menu-drawer"
        aria-hidden={!open}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-[#1d3d3a] to-[#244c48] text-white p-5 flex items-center justify-between shrink-0">
          {user ? (
            <button
              onClick={() => go("/profilo")}
              className="flex items-center gap-3 text-left"
              data-testid="mobile-menu-user"
            >
              <img src={user.avatar || "https://i.pravatar.cc/80"} alt="avatar" className="w-12 h-12 rounded-full border-2 border-amber-400" />
              <div className="min-w-0">
                <div className="font-black truncate">{user.name}</div>
                <div className="text-xs text-amber-50/70 truncate">{user.email}</div>
              </div>
            </button>
          ) : (
            <div>
              <div className="text-xs text-amber-300 font-bold uppercase tracking-widest">San Marino Market</div>
              <div className="font-black mt-0.5">Menu</div>
            </div>
          )}
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            aria-label="Chiudi menu"
            data-testid="mobile-menu-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {/* Primary CTAs */}
          <div className="p-4 space-y-2 border-b border-slate-100">
            <MenuRow onClick={() => go("/pubblica")} icon={Plus} label="Vendi ora / Pubblica annuncio" highlight />
            <MenuRow onClick={() => go("/aste")} icon={Gavel} label="Aste Live" />
            {user && (
              <MenuRow onClick={() => go("/messaggi")} icon={MessageCircle} label="I miei messaggi" />
            )}
          </div>

          {/* Auth section */}
          {!user ? (
            <div className="p-4 space-y-2 border-b border-slate-100">
              <MenuRow onClick={() => go("/login")} icon={LogIn} label="Accedi" />
              <MenuRow onClick={() => go("/scegli-account")} icon={UserPlus} label="Registrati" highlight />
            </div>
          ) : (
            <div className="p-4 space-y-2 border-b border-slate-100">
              <MenuRow onClick={() => go("/profilo")} icon={User} label="Il mio profilo" />
              {user.account_type === "business" && (
                <MenuRow
                  onClick={() => go("/azienda/dashboard")}
                  icon={Building2}
                  label="Dashboard Azienda"
                  highlight
                  testId="mobile-business-dashboard"
                />
              )}
              <MenuRow onClick={() => go("/preferiti")} icon={Heart} label="I miei preferiti" />
              {user.is_admin && (
                <MenuRow onClick={() => go("/admin")} icon={Crown} label="Admin Panel" highlight />
              )}
            </div>
          )}

          {/* Categories */}
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="w-4 h-4 text-[#e86a24]" />
              <div className="text-xs font-black uppercase tracking-widest text-slate-500">Categorie</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  onClick={() => go(`/categoria/${c.id}`)}
                  className="text-left text-xs font-bold text-slate-700 hover:text-[#e86a24] hover:bg-orange-50 rounded-lg px-3 py-2 transition-colors truncate"
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          {/* Footer links */}
          <div className="p-4 space-y-2">
            <MenuRow onClick={() => go("/")} icon={HomeIcon} label="Home" small />
            <MenuRow onClick={() => go("/privacy")} icon={Shield} label="Privacy Policy" small />
            <MenuRow onClick={() => go("/termini")} icon={FileText} label="Termini di servizio" small />
            <MenuRow onClick={() => go("/cookie")} icon={FileText} label="Cookie Policy" small />
            {user && (
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-colors font-semibold text-sm mt-2"
                data-testid="mobile-menu-logout"
              >
                <LogOut className="w-4 h-4" />
                Esci dall'account
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 text-center text-[10px] text-slate-500 shrink-0">
          © {new Date().getFullYear()} SAN MARINO MARKET ·{" "}
          <a href="https://ninfa.eu" target="_blank" rel="noopener noreferrer" className="text-amber-600 font-bold">@ninfa.eu</a>
        </div>
      </aside>
    </>
  );
}

function MenuRow({ onClick, icon: Icon, label, highlight, small, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center gap-3 px-3 py-${small ? "2" : "3"} rounded-xl transition-colors text-left ${
        highlight
          ? "bg-orange-50 text-[#e86a24] font-black hover:bg-orange-100"
          : "text-slate-700 font-semibold hover:bg-slate-50"
      }`}
    >
      <Icon className={`${small ? "w-4 h-4" : "w-5 h-5"} shrink-0`} />
      <span className={small ? "text-xs" : "text-sm"}>{label}</span>
    </button>
  );
}
